/**
 * Shared Intercom identity payload construction (ENG-869 / ENG-829).
 *
 * Every Artic SPA identifies the signed-in user to the Intercom Messenger with
 * the same three things — **Name, Email and Partner** — so the logic lives here
 * rather than being copy-pasted per app. Deliberately pure and free of any
 * `react-use-intercom` import: the payload is typed structurally, so this
 * package gains no dependency on the Messenger SDK and stays unit-testable.
 *
 * Each app keeps a thin `IntercomIdentity` component that reads
 * `useCurrentUser()` / `useIsArticAdmin()` and hands the result to
 * `update()`.
 */

import type {
  CurrentUserResponseDto,
  CurrentUserResponseDtoPartnersItem,
} from './generated/schemas/index.ts';

/**
 * An Intercom company. `companyId` and `name` are the minimum Intercom requires
 * to attach a contact to an account.
 */
export interface IntercomCompany {
  companyId: string;
  name: string;
}

/**
 * The subset of Intercom's contact attributes we send. `name` and `company` are
 * omitted entirely rather than sent empty — Intercom treats a present-but-blank
 * value as an instruction to overwrite whatever it already holds.
 */
export interface IntercomIdentity {
  userId: string;
  email: string;
  name?: string;
  company?: IntercomCompany;
}

/**
 * Artic staff can switch partner freely and are granted *every* partner by
 * `/current-user`, so attributing them to whichever partner they happen to be
 * viewing would smear internal users across every customer's company record and
 * inflate per-partner user counts. Pin them to one internal company instead, so
 * customer accounts only ever show genuine customer users.
 */
export const ARTIC_INTERNAL_COMPANY: IntercomCompany = {
  companyId: 'artic-internal',
  name: 'Artic',
};

/**
 * The canonical display-name chain: the profile's display name, else
 * "<first> <last>", else nothing.
 *
 * Whitespace is trimmed at every step, so a profile with a blank `profile_name`
 * still falls through to the first/last pair instead of yielding no name at all.
 */
export function userDisplayName(
  user: Pick<CurrentUserResponseDto, 'profile_name' | 'profile_first_name' | 'profile_last_name'>,
): string | undefined {
  const display = user.profile_name?.trim();
  if (display) return display;

  const composed = [user.profile_first_name, user.profile_last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return composed || undefined;
}

/**
 * Resolves the `artic_partner` cookie value to the full partner record.
 *
 * The cookie holds the short `partner_key` (e.g. "LOW"), so match that first,
 * then tolerate a cookie that already holds a `partner_name`, then fall back to
 * the user's first partner for a stale or unrecognised value. Same match order
 * as each app's `usePartnerName` / `usePartnerId`.
 */
export function resolveActivePartner(
  partners: CurrentUserResponseDtoPartnersItem[],
  activePartner: string | undefined,
): CurrentUserResponseDtoPartnersItem | undefined {
  if (partners.length === 0) return undefined;

  return (
    partners.find((partner) => partner.partner_key === activePartner) ??
    partners.find((partner) => partner.partner_name === activePartner) ??
    partners[0]
  );
}

/** The human-readable partner label, falling back to the unique identifier. */
export function partnerCompanyName(partner: CurrentUserResponseDtoPartnersItem): string {
  return partner.partner_display_name?.trim() || partner.partner_name;
}

export interface BuildIntercomIdentityArgs {
  user: CurrentUserResponseDto;
  /** The resolved active partner, from `resolveActivePartner`. */
  partner: CurrentUserResponseDtoPartnersItem | undefined;
  /** From `useIsArticAdmin()` — see `ARTIC_INTERNAL_COMPANY`. */
  isArticAdmin: boolean;
}

/**
 * Builds the Intercom `update` payload for the signed-in user.
 *
 * `companyId` is the partner **UUID**, not the `partner_key`, so an Intercom
 * account survives a partner being renamed or re-keyed.
 */
export function buildIntercomIdentity({
  user,
  partner,
  isArticAdmin,
}: BuildIntercomIdentityArgs): IntercomIdentity {
  const name = userDisplayName(user);

  let company: IntercomCompany | undefined;
  if (isArticAdmin) {
    company = ARTIC_INTERNAL_COMPANY;
  } else if (partner) {
    company = { companyId: partner.partner_id, name: partnerCompanyName(partner) };
  }

  return {
    userId: user.id,
    email: user.email,
    ...(name ? { name } : {}),
    ...(company ? { company } : {}),
  };
}
